import React, { useEffect, useState } from "react";

import type { Deployment } from "../../../types/strategyDeploy";
import { GREEN, RED, SURF } from "./liveStrategiesFormat";

const MINUTES: Record<string, number> = { "1m": 1, "5m": 5, "15m": 15, "30m": 30, "1h": 60, "2h": 120, "4h": 240, "1d": 1440 };

function candleMinutes(ct: string): number {
  if (MINUTES[ct]) return MINUTES[ct];
  const m = /^(\d+)([mhd])$/.exec(ct);
  if (!m) return 5;
  return Number(m[1]) * (m[2] === "m" ? 1 : m[2] === "h" ? 60 : 1440);
}

function mmss(ms: number): string {
  const s = Math.max(0, Math.round(ms / 1000));
  if (s >= 3600) return `${Math.floor(s / 3600)}h ${Math.floor((s % 3600) / 60)}m`;
  return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, "0")}`;
}

export type HeartbeatState = "healthy" | "late" | "idle" | "listening";

/** Is the deployment alive? Last candle checked, when the next check is due, and red when it's late. */
export function heartbeat(dep: Deployment, now: number): { state: HeartbeatState; text: string } {
  if (dep.status !== "running") return { state: "idle", text: dep.status === "paused" ? "Paused" : "Not running" };
  if (dep.source === "pine") return { state: "listening", text: "Listening for TradingView alerts" };
  const tf = candleMinutes(dep.candle_type) * 60_000;
  const nextClose = Math.floor(now / tf) * tf + tf;
  const last = dep.last_bar_open ? new Date(dep.last_bar_open).getTime() : null;
  // The candle that closed last should be checked within a couple of minutes of its close.
  const due = Math.floor(now / tf) * tf - tf;
  // Late = the candle after the last one checked closed (last + 2 tf) more than 3 minutes ago.
  const late = last == null ? now - new Date(dep.created_at).getTime() > tf + 180_000 : last < due && now - (last + 2 * tf) > 180_000;
  if (late) {
    const since = dep.last_evaluated_at ? new Date(dep.last_evaluated_at) : null;
    return {
      state: "late",
      text: since ? `No check since ${since.toLocaleTimeString(undefined, { hour: "2-digit", minute: "2-digit" })}` : "Not checked yet",
    };
  }
  const ran = dep.last_evaluated_at ? new Date(dep.last_evaluated_at) : null;
  const checked = ran
    ? `Last check ${ran.toLocaleTimeString(undefined, { hour: "2-digit", minute: "2-digit" })}`
    : "Starting";
  return { state: "healthy", text: `${checked} · next in ${mmss(nextClose - now)}` };
}

export function useNow(intervalMs = 1000): number {
  const [now, setNow] = useState(Date.now());
  useEffect(() => {
    const t = setInterval(() => setNow(Date.now()), intervalMs);
    return () => clearInterval(t);
  }, [intervalMs]);
  return now;
}

/** Pulse + line for a deployment card / header. */
export function Heartbeat({ dep, className }: { dep: Deployment; className?: string }): JSX.Element {
  const now = useNow();
  const hb = heartbeat(dep, now);
  const color = hb.state === "late" ? RED : hb.state === "healthy" ? GREEN : hb.state === "listening" ? SURF : "var(--muted-foreground)";
  return (
    <span
      className={`inline-flex min-h-7 min-w-0 items-center gap-2 self-center font-[family-name:var(--font-primary)] text-[13px] leading-none ${className ?? ""}`}
      data-live-heartbeat={hb.state}
    >
      <span className="relative flex size-2.5 shrink-0">
        {hb.state === "healthy" || hb.state === "listening" ? (
          <span className="absolute inline-flex size-full animate-ping rounded-full opacity-60" style={{ background: color }} />
        ) : null}
        <span className="relative inline-flex size-2.5 rounded-full" style={{ background: color }} />
      </span>
      <span className="truncate tabular-nums" style={{ color: hb.state === "late" ? RED : undefined }}>
        {hb.text}
      </span>
    </span>
  );
}
